import type { LocationPlaceValue } from '@repo/features/location'
import type { TimePickerValue } from '@repo/features/time-picker'

import { calendarDayNumber } from './calendar-date.ts'
import { getEarliestAllowedDate, isValidDate, isValidRouteStructure } from './route-model.ts'
import type { QuickCreateFormValues, QuickCreateValidationIssue } from './values.ts'

const MAX_INT32_VALUE = 2_147_483_647

const isValidPlace = (place: LocationPlaceValue | null) =>
  place?.location_id != null && Number.isFinite(place.location_id)

const isValidClockTime = (time: null | string | undefined) =>
  time != null && /^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(time)

const isValidQuickCreateTime = (time: TimePickerValue) => {
  switch (time.type) {
    case '24':
      return false
    case 'asap':
      return true
    case 'at':
      return isValidClockTime(time.from)
    case 'between':
      return isValidClockTime(time.from) && isValidClockTime(time.to)
    case 'by':
      return isValidClockTime(time.to)
    case 'tbc':
      return true
  }
}

const DATE_ORDER_ERROR = 'Date must be on or after the previous Stop date.'

const isValidWeight = (weight: number | undefined) =>
  weight === undefined ||
  (Number.isFinite(weight) && Number.isInteger(weight) && weight >= 1 && weight <= MAX_INT32_VALUE)

export const getQuickCreateValidationIssues = (
  values: QuickCreateFormValues,
): QuickCreateValidationIssue[] => {
  const issues: QuickCreateValidationIssue[] = []

  if (!isValidRouteStructure(values.drops)) {
    issues.push({
      message:
        'A load needs at least two Stops, beginning with a Collection and ending with a Delivery.',
      path: 'drops',
    })
  }

  if (values.organisation_id == null) {
    issues.push({ message: 'Please select an organisation.', path: 'organisation_id' })
  }
  if (values.company_id == null) {
    issues.push({ message: 'Please select a customer.', path: 'company_id' })
  }
  if (values.requires_end_customer && values.end_customer_company_id == null) {
    issues.push({
      message: 'Please select an end customer company.',
      path: 'end_customer_company_id',
    })
  }

  values.drops.forEach((stop, index) => {
    if (!isValidPlace(stop.place)) {
      issues.push({ message: 'Select a saved company location.', path: `drops.${index}.place` })
    }
    if (!isValidDate(stop.date)) {
      issues.push({ message: 'Select a date.', path: `drops.${index}.date` })
    }
    if (!isValidQuickCreateTime(stop.time)) {
      issues.push({ message: 'Enter a valid time.', path: `drops.${index}.time` })
    }

    const earliestDate = getEarliestAllowedDate(values.drops, index)
    if (
      isValidDate(stop.date) &&
      earliestDate &&
      calendarDayNumber(stop.date) < calendarDayNumber(earliestDate)
    ) {
      issues.push({ message: DATE_ORDER_ERROR, path: `drops.${index}.date` })
    }
  })

  if (values.load_type_id == null) {
    issues.push({ message: 'Please select a vehicle type.', path: 'load_type_id' })
  }

  if (!isValidWeight(values.weight)) {
    issues.push({
      message: 'Weight must be a whole number between 1 and 2,147,483,647.',
      path: 'weight',
    })
  }

  if (values.customer_rate_amount.trim() !== '') {
    const rate = Number(values.customer_rate_amount)
    if (!Number.isFinite(rate) || rate <= 0) {
      issues.push({ message: 'Rate must be greater than 0.', path: 'customer_rate_amount' })
    }
  }

  return issues
}

const getFormFieldPath = (path: string) => {
  const dropMatch = /^drops\.(\d+)\.(date|place|time)$/.exec(path)
  if (dropMatch) return `drops[${dropMatch[1]}].${dropMatch[2]}`

  return path
}

const getQuickCreateFieldValidationErrorsForIssues = (
  issues: readonly QuickCreateValidationIssue[],
) => {
  const fieldErrors = new Map<string, Array<{ message: string }>>()

  for (const issue of issues) {
    const path = getFormFieldPath(issue.path)
    const existing = fieldErrors.get(path)

    if (existing) {
      existing.push({ message: issue.message })
    } else {
      fieldErrors.set(path, [{ message: issue.message }])
    }
  }

  return Object.fromEntries(fieldErrors)
}

export const getQuickCreateFieldValidationErrors = (values: QuickCreateFormValues) =>
  getQuickCreateFieldValidationErrorsForIssues(getQuickCreateValidationIssues(values))

export const getQuickCreateChronologyValidationErrors = (values: QuickCreateFormValues) =>
  getQuickCreateFieldValidationErrorsForIssues(
    getQuickCreateValidationIssues(values).filter(issue => issue.message === DATE_ORDER_ERROR),
  )
